// Sectorpagina's: "Website laten maken voor een [sector]".
// Dit is de sterkste SEO-baan uit het onderzoek: vrijwel geen concurrentie,
// directe koopintentie, en per sector hebben we een echte case als bewijs.
// Nieuwe sector toevoegen betekent een item toevoegen aan deze array.
// De pagina en de route komen er automatisch bij.

export type SectorPage = {
  slug: string
  sector: string
  h1: string
  metaTitle: string
  metaDescription: string
  intro: string
  // De situatie waarin deze ondernemer zich herkent
  herkenbaar: string[]
  // Wat er op de site van deze sector thuishoort
  onderdelen: { titel: string; tekst: string }[]
  // Waarom vindbaarheid in deze sector anders werkt
  vindbaarheid: string
  // Slug van de klantcase die als bewijs dient
  caseSlug?: string
  caseNaam?: string
  caseTekst?: string
  faq: { vraag: string; antwoord: string }[]
}

export const sectorPages: SectorPage[] = [
  {
    slug: 'fysiotherapiepraktijk',
    sector: 'Fysiotherapie',
    h1: 'Website laten maken voor een fysiotherapiepraktijk',
    metaTitle:
      'Website laten maken voor een fysiotherapiepraktijk | Noord Digital',
    metaDescription:
      'Een praktijkwebsite met een eigen pagina per behandeling, gekoppeld patiëntenportaal en vacaturemodule. Gebouwd voor praktijken in Noord-Holland die gevonden willen worden op klacht en op wijk.',
    intro:
      'Een fysiotherapiepraktijk heeft een probleem dat de meeste bedrijven niet kennen: er is te veel om te vertellen. Met tien tot vijftien specialisaties raakt een bezoeker verdwaald voordat hij bij zijn eigen klacht is. En wie zoekt op oedeemfysiotherapie wil niet eerst door een algemeen verhaal over de praktijk heen.',
    herkenbaar: [
      'Je hebt meerdere specialisaties, maar op de site staan ze op één pagina onder elkaar.',
      'Patiënten bellen voor dingen die ze zelf online zouden kunnen regelen.',
      'Je zoekt collega’s, maar vacatures staan alleen op een vacaturebank en niet op je eigen site.',
      'Je weet niet of mensen je vinden op de klacht waar je juist goed in bent.',
    ],
    onderdelen: [
      {
        titel: 'Een eigen pagina per behandeling',
        tekst:
          'Iemand die zoekt op dry needling, oedeemfysiotherapie of revalidatie na een knieoperatie landt direct op die behandeling. Dat is de enige manier om op die termen gevonden te worden, want één verzamelpagina rankt nergens goed op.',
      },
      {
        titel: 'Koppeling met je patiëntenportaal',
        tekst:
          'Afspraken, dossier en vragenlijsten via een directe koppeling met je praktijksoftware. Dat scheelt telefoontjes en patiënten regelen het op het moment dat het hun uitkomt.',
      },
      {
        titel: 'Vacatures op je eigen site',
        tekst:
          'Een eigen pagina per functie, zodat werving via je praktijk loopt en niet via een portal waar je concurrent ernaast staat.',
      },
      {
        titel: 'Kwaliteitssignalen zichtbaar',
        tekst:
          'BIG-registratie, KNGF-aansluiting en de zorgnetwerken waar je bij hoort. Voor een patiënt die twijfelt is dat het verschil.',
      },
      {
        titel: 'Tarieven en vergoedingen',
        tekst:
          'De vraag die iedereen stelt en waar veel praktijken vaag over blijven. Helderheid vooraf scheelt gesprekken achteraf.',
      },
    ],
    vindbaarheid:
      'Patiënten zoeken op twee manieren: op klacht en op plaats. “Fysiotherapeut rugklachten Alkmaar” is een andere zoekopdracht dan “oncologiefysiotherapie Noord-Holland”, en die vragen om verschillende pagina’s. Door per behandeling een pagina te maken en die lokaal te verankeren, dek je beide af.',
    caseSlug: 'fysiotherapiepraktijk-cg',
    caseNaam: 'Fysiotherapiepraktijk CG',
    caseTekst:
      'Veertien behandelpagina’s, een gekoppeld patiëntenportaal, een vacaturemodule en een blog waarin de therapeuten zelf uitleg geven.',
    faq: [
      {
        vraag: 'Kan het patiëntenportaal gekoppeld worden?',
        antwoord:
          'Ja. We hebben ervaring met de koppeling naar Intramed en kunnen ook met andere praktijksoftware uit de voeten. De patiënt logt in vanaf jouw site en komt in de vertrouwde omgeving terecht.',
      },
      {
        vraag: 'Hoeveel behandelpagina’s heb ik nodig?',
        antwoord:
          'Elke specialisatie waar je daadwerkelijk op gevonden wilt worden verdient een eigen pagina. Bij de meeste praktijken komt dat neer op acht tot vijftien. Behandelingen die je wel aanbiedt maar niet actief wilt werven, kunnen op een verzamelpagina.',
      },
      {
        vraag: 'Mag ik patiëntverhalen op de site zetten?',
        antwoord:
          'Alleen met expliciete toestemming en zonder herleidbare gegevens. We werken liever met geanonimiseerde trajectbeschrijvingen, want die geven hetzelfde vertrouwen zonder privacyrisico.',
      },
    ],
  },
  {
    slug: 'sportschool',
    sector: 'Sportschool',
    h1: 'Website laten maken voor een sportschool of gym',
    metaTitle: 'Website laten maken voor een sportschool | Noord Digital',
    metaDescription:
      'Een sportschoolwebsite met rooster, abonnementen en online proefles-aanmelding. Voor gyms in Noord-Holland die nieuwe leden uit de buurt willen aantrekken.',
    intro:
      'Bij een sportschool telt één ding: hoeveel mensen zich deze maand aanmelden voor een proefles. Alles op de site hoort daaraan bij te dragen. In de praktijk zien we vaak het omgekeerde: een mooie site waar het rooster drie klikken diep zit en aanmelden alleen per telefoon kan.',
    herkenbaar: [
      'Je krijgt telefoontjes over openingstijden en lestijden die op de site zouden moeten staan.',
      'Aanmelden voor een proefles gaat via een mail die soms blijft liggen.',
      'Je bent online vooral zichtbaar via Facebook of Instagram, niet via Google.',
      'Nieuwe leden komen bijna allemaal via mond-tot-mond uit de directe omgeving.',
    ],
    onderdelen: [
      {
        titel: 'Rooster dat altijd klopt',
        tekst:
          'Lestijden direct zichtbaar op de eerste schermhoogte. Dit is veruit de meest gezochte informatie op een sportschoolsite.',
      },
      {
        titel: 'Proefles boeken in twee stappen',
        tekst:
          'Een formulier dat direct bij jou binnenkomt en bij voorkeur meteen in je ledenadministratie. Hoe minder stappen, hoe meer aanmeldingen.',
      },
      {
        titel: 'Abonnementen helder naast elkaar',
        tekst:
          'Prijzen zonder verrassingen. Wie de prijs niet kan vinden, gaat ervan uit dat hij te hoog is.',
      },
      {
        titel: 'Sfeer die klopt met de zaal',
        tekst:
          'Echte foto’s van jouw zaal en jouw leden. Stockfoto’s van een Amerikaanse gym werken averechts, want mensen willen zien waar ze binnenstappen.',
      },
      {
        titel: 'De trainers in beeld',
        tekst:
          'Wie ga je tegenkomen? Bij een kleine gym is dat vaak de doorslaggevende reden om te kiezen.',
      },
    ],
    vindbaarheid:
      'Een sportschool wordt gevonden op afstand. Vrijwel niemand rijdt twintig minuten voor een gym. Dat maakt lokale vindbaarheid belangrijker dan alles: een goed ingericht Google Bedrijfsprofiel, actuele openingstijden en pagina’s die de wijken en dorpen benoemen waar je leden vandaan komen.',
    caseSlug: 'gymfit-de-noord',
    caseNaam: 'GymFit de Noord',
    caseTekst:
      'Eerste online aanwezigheid voor een crossfit gym, met lessenrooster en ledeninschrijving direct in de site geïntegreerd.',
    faq: [
      {
        vraag: 'Kan het rooster automatisch bijwerken?',
        antwoord:
          'Ja, als je ledenadministratie een koppeling toestaat. Zo niet, dan bouwen we een beheeromgeving waarin je het rooster zelf in een paar minuten aanpast.',
      },
      {
        vraag: 'Moeten prijzen op de site?',
        antwoord:
          'Wij adviseren van wel. Bezoekers die de prijs niet kunnen vinden haken af of nemen contact op puur om die vraag te stellen. Beide kosten je meer dan transparantie.',
      },
      {
        vraag: 'Kunnen leden zelf inschrijven voor lessen?',
        antwoord:
          'Dat kan, via een koppeling met een reserveringssysteem. Voor kleinere gyms is een eenvoudig aanmeldformulier vaak voldoende en een stuk goedkoper in onderhoud.',
      },
    ],
  },
  {
    slug: 'schoonmaakbedrijf',
    sector: 'Schoonmaak',
    h1: 'Website laten maken voor een schoonmaakbedrijf',
    metaTitle: 'Website laten maken voor een schoonmaakbedrijf | Noord Digital',
    metaDescription:
      'Een schoonmaaksite die zakelijke opdrachten oplevert in plaats van losse klussen. Heldere diensten, snelle offerteaanvraag en lokale vindbaarheid.',
    intro:
      'In de schoonmaak is de concurrentie groot en het onderscheid klein. Iedereen zegt grondig en betrouwbaar te zijn. De winst zit niet in mooiere woorden, maar in het scheiden van je doelgroepen en het verlagen van de drempel om contact op te nemen.',
    herkenbaar: [
      'Je krijgt vooral particuliere aanvragen, terwijl de marge in zakelijke contracten zit.',
      'Offertes aanvragen gaat via een algemeen contactformulier zonder enige context.',
      'Je wordt gevonden op je bedrijfsnaam, maar niet op wat je doet.',
      'Grotere opdrachtgevers nemen je niet serieus omdat de online uitstraling achterloopt.',
    ],
    onderdelen: [
      {
        titel: 'Zakelijk en particulier gescheiden',
        tekst:
          'Een kantoormanager en een particulier zoeken iets anders en willen andere informatie. Twee routes vanaf de eerste sectie voorkomt dat je beide half bedient.',
      },
      {
        titel: 'Een pagina per dienst',
        tekst:
          'Kantoorschoonmaak, opleveringsschoonmaak, wisselschoonmaak voor vakantieparken en glasbewassing zijn verschillende zoekopdrachten met verschillende koopmomenten.',
      },
      {
        titel: 'Werkwijze in stappen',
        tekst:
          'Wat gebeurt er nadat iemand belt? Opdrachtgevers willen weten waar ze aan toe zijn voordat ze een offerte aanvragen.',
      },
      {
        titel: 'Offerte aanvragen zonder drempel',
        tekst:
          'Bellen en het formulier altijd binnen bereik, op elke schermhoogte. In deze branche wordt vaak direct gebeld.',
      },
      {
        titel: 'Bewijs van betrouwbaarheid',
        tekst:
          'Verzekering, VOG, referenties en het aantal panden dat je onderhoudt. Je komt binnen bij mensen die er niet zijn, dus vertrouwen is het product.',
      },
    ],
    vindbaarheid:
      'Zakelijke schoonmaakopdrachten worden gezocht op combinaties van dienst en plaats, bijvoorbeeld “kantoorschoonmaak Alkmaar” of “opleveringsschoonmaak na verbouwing Noord-Holland”. Dat zijn termen met weinig volume en weinig concurrentie, en precies daarom haalbaar.',
    caseSlug: 'ars-cleanex',
    caseNaam: 'Ars Cleanex',
    caseTekst:
      'Een strakke onepager voor een zelfstandig schoonmaakbedrijf, met zes diensten en een werkwijze in vier stappen.',
    faq: [
      {
        vraag: 'Is een onepager genoeg voor een schoonmaakbedrijf?',
        antwoord:
          'Voor een zelfstandige die vooral op naam en aanbeveling werkt vaak wel. Wil je gevonden worden op verschillende diensten, dan heb je per dienst een pagina nodig en wordt het een meerpaginasite.',
      },
      {
        vraag: 'Moet ik tarieven vermelden?',
        antwoord:
          'In de schoonmaak zelden, omdat de prijs afhangt van oppervlakte en frequentie. Wel adviseren we een indicatie of een rekenvoorbeeld, zodat bezoekers weten of ze in de goede orde van grootte zitten.',
      },
      {
        vraag: 'Hoe krijg ik meer zakelijke aanvragen?',
        antwoord:
          'Door zakelijk en particulier te scheiden en door pagina’s te maken voor de diensten waar bedrijven op zoeken. Een algemene schoonmaakpagina trekt vooral particulieren.',
      },
    ],
  },
  {
    slug: 'uitzendbureau',
    sector: 'Uitzendbureau',
    h1: 'Website laten maken voor een uitzendbureau',
    metaTitle: 'Website laten maken voor een uitzendbureau | Noord Digital',
    metaDescription:
      'Een uitzendsite die twee doelgroepen tegelijk bedient: kandidaten die werk zoeken en opdrachtgevers die personeel nodig hebben. Met een eigen pagina per functie.',
    intro:
      'Een uitzendbureau moet twee compleet verschillende markten bedienen op dezelfde site. Kandidaten willen weten wat het betaalt en hoe flexibel het is. Opdrachtgevers willen weten of je snel kunt leveren. Eén boodschap voor beide werkt voor geen van beide.',
    herkenbaar: [
      'Je site spreekt vooral opdrachtgevers aan, terwijl je vooral kandidaten tekortkomt.',
      'Vacatures staan in een lijst zonder eigen pagina, dus ze zijn niet apart vindbaar.',
      'Aanvragen komen ongesorteerd binnen en je weet pas na het openen om wie het gaat.',
      'Je concurreert met bureaus die veel meer advertentiebudget hebben.',
    ],
    onderdelen: [
      {
        titel: 'Twee routes vanaf de eerste sectie',
        tekst:
          'Kom werken bij ons, of huur personeel in. De bezoeker kiest zelf en krijgt daarna alleen wat voor hem relevant is.',
      },
      {
        titel: 'Een eigen pagina per functie',
        tekst:
          'Bartender, keukenhulp, verkeersregelaar: iemand die op die functie zoekt moet daar landen. Voor een uitzendbureau is dit geen bijzaak maar het verdienmodel.',
      },
      {
        titel: 'Ervaringen van je eigen mensen',
        tekst:
          'Kandidaten gaan af op wat collega’s zeggen over betaling en sfeer. Dat weegt zwaarder dan wat het bureau zelf beweert.',
      },
      {
        titel: 'Formulier met routing',
        tekst:
          'De bezoeker geeft eerst aan of hij werk zoekt of personeel. Aanvragen komen daardoor voorgesorteerd binnen.',
      },
      {
        titel: 'Duidelijkheid over betaling',
        tekst:
          'Wanneer wordt er uitbetaald en hoe vaak? In deze branche is dat de meest gestelde vraag en het sterkste onderscheid.',
      },
    ],
    vindbaarheid:
      'De grootste winst zit in functiepagina’s gecombineerd met de regio. “Bijbaan bartender Noord-Holland” of “horecapersoneel inhuren Alkmaar” zijn haalbare termen, terwijl je op “uitzendbureau” nooit gaat winnen van de landelijke ketens.',
    caseSlug: '5-stars-events',
    caseNaam: '5* Stars Events',
    caseTekst:
      'Tien eigen rolpagina’s en een contactformulier met routing, zodat werving en acquisitie beide via de site lopen.',
    faq: [
      {
        vraag: 'Hoeveel functiepagina’s heb ik nodig?',
        antwoord:
          'Eén per functie waarop je structureel werft. Tien tot vijftien is gebruikelijk. Functies die je zelden invult kun je samenvoegen op een overzichtspagina.',
      },
      {
        vraag: 'Kan de site koppelen met mijn planningssysteem?',
        antwoord:
          'Vaak wel. Aanmeldingen kunnen rechtstreeks in je systeem terechtkomen in plaats van in een mailbox. Dat scheelt overtypen en fouten.',
      },
      {
        vraag: 'Moet ik tarieven voor opdrachtgevers tonen?',
        antwoord:
          'Meestal niet, want die zijn afhankelijk van functie en volume. Wel adviseren we duidelijk te maken hoe snel je kunt leveren, want daar draait de keuze om.',
      },
    ],
  },
  {
    slug: 'hovenier',
    sector: 'Hovenier',
    h1: 'Website laten maken voor een hovenier',
    metaTitle: 'Website laten maken voor een hovenier | Noord Digital',
    metaDescription:
      'Een hoveniersite waarin je werk het verhaal vertelt. Projectfoto’s, een heldere dienstenstructuur en lokale vindbaarheid in Noord-Holland.',
    intro:
      'Een hovenier verkoopt iets dat mensen willen zien voordat ze bellen. Je vakmanschap staat in de tuinen die je hebt aangelegd, en die moeten dus op je site staan. Toch zien we vaak sites vol tekst met drie kleine foto’s onderaan.',
    herkenbaar: [
      'Je mooiste projecten staan op je telefoon en niet op je site.',
      'Je krijgt aanvragen voor werk dat je liever niet doet.',
      'Mensen vragen naar prijzen zonder enig idee van de orde van grootte.',
      'Je bent lokaal bekend, maar buiten je dorp kent niemand je.',
    ],
    onderdelen: [
      {
        titel: 'Projecten als hoofdmoot',
        tekst:
          'Voor en na, met een korte beschrijving van de opgave. Dit is je sterkste verkoopargument en het hoort niet onderaan te staan.',
      },
      {
        titel: 'Diensten uit elkaar getrokken',
        tekst:
          'Ontwerp, aanleg en onderhoud zijn verschillende opdrachten met verschillende klanten. Wie onderhoud zoekt, wil geen ontwerpverhaal lezen.',
      },
      {
        titel: 'Je aanpak en je visie',
        tekst:
          'Werk je ecologisch, met inheemse beplanting of juist strak en onderhoudsarm? Dat filtert vooraf en levert betere aanvragen op.',
      },
      {
        titel: 'Indicatie van investering',
        tekst:
          'Geen prijslijst, wel een orde van grootte per type project. Dat voorkomt gesprekken met mensen die een heel ander budget in gedachten hadden.',
      },
      {
        titel: 'Werkgebied benoemd',
        tekst:
          'Welke plaatsen bedien je? Dit is zowel praktische informatie als de basis voor je lokale vindbaarheid.',
      },
    ],
    vindbaarheid:
      'Hoveniers worden gezocht op plaats en op type werk. “Tuin laten aanleggen Heerhugowaard” of “ecologische hovenier Noord-Holland” zijn concreter en beter haalbaar dan het brede “hovenier”, waar je landelijk tegen platforms aanloopt.',
    caseSlug: 'de-tuinvrouw',
    caseNaam: 'De Tuinvrouw',
    caseTekst:
      'Een site waarin de ecologische aanpak het uitgangspunt is, met veel ruimte voor fotografie van gerealiseerde tuinen.',
    faq: [
      {
        vraag: 'Hoeveel projecten moet ik laten zien?',
        antwoord:
          'Acht tot twaalf goed gefotografeerde projecten werkt beter dan dertig snapshots. Kwaliteit weegt hier zwaarder dan aantal.',
      },
      {
        vraag: 'Moet ik prijzen noemen?',
        antwoord:
          'Een orde van grootte per projecttype scheelt je veel gesprekken die nergens toe leiden. Exacte prijzen zijn onmogelijk zonder de tuin te zien.',
      },
      {
        vraag: 'Kan ik zelf projecten toevoegen?',
        antwoord:
          'Ja, we richten het zo in dat je zelf foto’s en een beschrijving kunt toevoegen zonder ons nodig te hebben.',
      },
    ],
  },
  {
    slug: 'renovatiebedrijf',
    sector: 'Renovatie en bouw',
    h1: 'Website laten maken voor een renovatiebedrijf',
    metaTitle: 'Website laten maken voor een renovatiebedrijf | Noord Digital',
    metaDescription:
      'Een site die vakmanschap zichtbaar maakt en grotere opdrachten dichterbij brengt. Voor renovatiebedrijven en aannemers in Noord-Holland.',
    intro:
      'Veel renovatiebedrijven draaien volledig op mond-tot-mondreclame. Dat werkt, tot je grotere opdrachten wilt. Een zakelijke opdrachtgever of een vve googelt je naam voordat hij belt, en wat hij dan vindt bepaalt of het gesprek doorgaat.',
    herkenbaar: [
      'Wie je naam googelt komt vrijwel niets tegen.',
      'Je werk staat op Instagram maar niet op een eigen plek die je bezit.',
      'Je krijgt vooral kleine klussen terwijl je grotere projecten aankunt.',
      'Offerteportals leveren aanvragen op waar je op prijs moet concurreren.',
    ],
    onderdelen: [
      {
        titel: 'Projecten met bewijs',
        tekst:
          'Foto’s van opgeleverd werk, met wat de opgave was en hoe lang het duurde. Dat is wat een opdrachtgever wil weten voordat hij belt.',
      },
      {
        titel: 'Specialisaties apart',
        tekst:
          'Badkamerrenovatie, tegelwerk, stucwerk en totaalrenovatie zijn verschillende zoekopdrachten. Elk verdient een eigen plek.',
      },
      {
        titel: 'De werkwijze in stappen',
        tekst:
          'Van eerste bezoek tot oplevering. Bij een verbouwing in iemands huis is voorspelbaarheid het belangrijkste dat je verkoopt.',
      },
      {
        titel: 'Eén aanspreekpunt benoemen',
        tekst:
          'Wie is de contactpersoon en is die bereikbaar tijdens het werk? Dit is de meest voorkomende klacht in de branche en dus je beste onderscheid.',
      },
      {
        titel: 'Verzekering en garantie',
        tekst:
          'Voor zakelijke opdrachtgevers is dit een selectiecriterium, niet een detail.',
      },
    ],
    vindbaarheid:
      'De winst zit in specialisatie plus plaats: “badkamer renoveren Alkmaar” of “stucwerk Dijk en Waard”. Op het brede “aannemer” loop je tegen offerteportals aan met enorme budgetten, en dat gevecht is niet te winnen.',
    caseSlug: 'alex-construct',
    caseNaam: 'Alex Construct',
    caseTekst:
      'Van geen enkele online aanwezigheid naar een site die het vakmanschap laat zien, met hosting en zakelijke email erbij.',
    faq: [
      {
        vraag: 'Ik heb geen goede foto’s, kan het dan toch?',
        antwoord:
          'We kunnen starten met wat er is en later vervangen. Wel adviseren we bij het eerstvolgende project bewust te fotograferen, want beeld is in deze branche je belangrijkste verkoopmiddel.',
      },
      {
        vraag: 'Is een offerteportal niet makkelijker?',
        antwoord:
          'Die levert aanvragen, maar je concurreert daar met vier anderen op prijs. Een eigen site levert minder maar betere aanvragen op, van mensen die specifiek jou willen.',
      },
      {
        vraag: 'Moet ik een prijslijst tonen?',
        antwoord:
          'Nee, maar wel een indicatie per type project. Wie geen enkel idee heeft van de kosten belt niet, of belt met verkeerde verwachtingen.',
      },
    ],
  },
  {
    slug: 'sportvereniging',
    sector: 'Sportvereniging',
    h1: 'Website laten maken voor een sportvereniging',
    metaTitle: 'Website laten maken voor een sportvereniging | Noord Digital',
    metaDescription:
      'Een verenigingssite die leden werft, vrijwilligers aantrekt en sponsors zichtbaar maakt. Met rooster, tarieven en online aanmelding.',
    intro:
      'Een vereniging heeft geen marketingbudget en meestal ook geen webbeheerder. Wat er wel is, is een bestuur dat het erbij doet. De site moet dus zelfstandig werken: leden werven, vrijwilligers aantrekken en sponsors laten zien, zonder dat er wekelijks iemand aan moet sleutelen.',
    herkenbaar: [
      'De site is jaren geleden gebouwd door een ouder die inmiddels weg is.',
      'Trainingstijden staan op drie plekken en nergens hetzelfde.',
      'Nieuwe leden melden zich via een mailadres dat niemand structureel bijhoudt.',
      'Sponsors zien niet terug wat ze ervoor krijgen.',
    ],
    onderdelen: [
      {
        titel: 'Trainingstijden en locaties',
        tekst:
          'De meest bezochte informatie op elke verenigingssite. Direct zichtbaar, niet weggestopt in een pdf.',
      },
      {
        titel: 'Proeftraining aanmelden',
        tekst:
          'Een gratis proefles is de laagste drempel die er is. Zorg dat aanmelden in twee stappen kan.',
      },
      {
        titel: 'Het verhaal achter de aanpak',
        tekst:
          'Ouders kiezen op basis van hoe er met hun kind wordt omgegaan. Leg uit wat je methode is en waarom die anders is.',
      },
      {
        titel: 'Contributie zonder omwegen',
        tekst:
          'Wat kost het per maand en wat komt er nog bij aan wedstrijden of licenties? Onduidelijkheid hierover kost aanmeldingen.',
      },
      {
        titel: 'Sponsors in beeld',
        tekst:
          'Een sponsorblok met open plekken maakt het makkelijker om nieuwe partners te werven, want ze zien direct hoe ze eruit komen te staan.',
      },
    ],
    vindbaarheid:
      'Ouders zoeken op sport plus plaats: “zwemles Oosterhout” of “voetbalvereniging Dijk en Waard”. Daarnaast wordt er gezocht op leeftijd en niveau. Een pagina per leeftijdsgroep of programma dekt die vragen af.',
    caseSlug: 'aurora-swimming',
    caseNaam: 'Aurora Swimming',
    caseTekst:
      'Een door ons gesponsorde site voor een zwemclub in oprichting, met een programma per leeftijdsgroep, sponsorwerving en een donatiemodule.',
    faq: [
      {
        vraag: 'Wij hebben geen budget, wat kost dit?',
        antwoord:
          'Voor verenigingen kijken we naar wat haalbaar is en we hebben ook projecten gesponsord. Bespreek het gewoon, dan zoeken we samen naar iets dat past.',
      },
      {
        vraag: 'Kan het bestuur zelf dingen aanpassen?',
        antwoord:
          'Ja. We richten het zo in dat trainingstijden, nieuws en sponsors zonder technische kennis bij te werken zijn, want bij een vereniging wisselt het bestuur.',
      },
      {
        vraag: 'Kunnen we donaties ontvangen via de site?',
        antwoord:
          'Dat kan, met iDEAL en andere betaalmethoden. Wel is er een zakelijke rekening en een betaalprovider nodig, dat regelen we samen.',
      },
    ],
  },
  {
    slug: 'evenementenbureau',
    sector: 'Evenementen',
    h1: 'Website laten maken voor een evenementenbureau',
    metaTitle: 'Website laten maken voor een evenementenbureau | Noord Digital',
    metaDescription:
      'Een evenementensite waarin je portfolio en je aanvraagformulier elkaar versterken. Met ruimte voor vacatures en crew-werving.',
    intro:
      'In de evenementenbranche koopt niemand op een beschrijving. Ze kopen op wat ze zien. Tegelijk heb je vaak crew nodig, en dat is een tweede doelgroep met heel andere vragen. Beide moeten op dezelfde site terechtkunnen zonder elkaar in de weg te zitten.',
    herkenbaar: [
      'Je beste werk staat in een map op je laptop en niet online.',
      'Aanvragen komen binnen zonder datum, locatie of budget.',
      'Crew werven gaat via appgroepen en dat schaalt niet mee.',
      'Opdrachtgevers vragen naar referenties die je per mail moet opsturen.',
    ],
    onderdelen: [
      {
        titel: 'Portfolio dat het werk verkoopt',
        tekst:
          'Beeld eerst, tekst daarna. Wie een evenement laat organiseren wil zien wat je eerder hebt neergezet.',
      },
      {
        titel: 'Aanvraagformulier met de juiste vragen',
        tekst:
          'Datum, locatie, aantal gasten en indicatie van budget. Dan komt een aanvraag binnen waar je iets mee kunt in plaats van een los bericht.',
      },
      {
        titel: 'Vacatures en crew-werving',
        tekst:
          'Een eigen pagina per rol, zodat mensen die werk zoeken je vinden en zich rechtstreeks aanmelden.',
      },
      {
        titel: 'Type evenementen apart',
        tekst:
          'Bruiloften, bedrijfsfeesten en festivals trekken andere klanten met andere budgetten. Scheiden levert betere aanvragen op.',
      },
      {
        titel: 'Referenties zichtbaar',
        tekst:
          'Namen van opdrachtgevers en locaties waar je hebt gewerkt. Dat is het bewijs dat je aankunt wat je belooft.',
      },
    ],
    vindbaarheid:
      'Er wordt gezocht op type evenement plus regio, bijvoorbeeld “bedrijfsfeest organiseren Noord-Holland”. Daarnaast is er een aparte zoekstroom van mensen die werk zoeken, en die kun je met functiepagina’s afvangen.',
    caseSlug: '5-stars-events',
    caseNaam: '5* Stars Events',
    caseTekst:
      'Een site met twee routes, tien functiepagina’s en een formulier dat aanvragen vooraf sorteert.',
    faq: [
      {
        vraag: 'Moet ik naar budget vragen in het formulier?',
        antwoord:
          'Wij adviseren van wel, met bandbreedtes in plaats van een exact bedrag. Dat filtert aanvragen die toch nergens toe leiden en scheelt je veel tijd.',
      },
      {
        vraag: 'Kan ik zelf projecten toevoegen?',
        antwoord:
          'Ja, dat richten we zo in dat je na een evenement zelf beeld en een korte beschrijving kunt plaatsen.',
      },
      {
        vraag: 'Hoe combineer ik klanten en crew op één site?',
        antwoord:
          'Met twee duidelijke routes vanaf de eerste sectie, en een contactformulier waarin de bezoeker eerst aangeeft waarvoor hij komt.',
      },
    ],
  },
  {
    slug: 'financieel-adviseur',
    sector: 'Financieel advies',
    h1: 'Website laten maken voor een financieel adviseur',
    metaTitle: 'Website laten maken voor een financieel adviseur | Noord Digital',
    metaDescription:
      'Een adviessite die vertrouwen wekt voordat het eerste gesprek begint. Met een pagina per specialisme en heldere uitleg van de werkwijze.',
    intro:
      'Financieel advies verkoopt op vertrouwen. Iemand vertrouwt je zijn hypotheek, zijn bedrijf of zijn pensioen toe. Dat vertrouwen begint bij wat hij online tegenkomt, en dat is vrijwel altijd voordat jullie elkaar hebben gesproken.',
    herkenbaar: [
      'Je klanten komen bijna allemaal via doorverwijzing.',
      'Je site vertelt wat je doet, maar niet voor wie je het doet.',
      'Je specialisme staat verstopt tussen algemene dienstverlening.',
      'Concurrenten met minder ervaring staan hoger in Google.',
    ],
    onderdelen: [
      {
        titel: 'Een pagina per specialisme',
        tekst:
          'Bedrijfsovername, waardering, hypotheek voor ondernemers of pensioen: elk specialisme heeft een eigen zoekvraag en een eigen klant.',
      },
      {
        titel: 'Situaties in plaats van diensten',
        tekst:
          'Mensen zoeken niet op “bedrijfswaardering” maar op “wat is mijn bedrijf waard bij verkoop”. Schrijf vanuit de situatie waarin ze zitten.',
      },
      {
        titel: 'De adviseur in beeld',
        tekst:
          'Foto, achtergrond en registraties. In deze branche kies je een persoon, niet een kantoor.',
      },
      {
        titel: 'Werkwijze en kosten',
        tekst:
          'Hoe verloopt een traject en wat kost het ongeveer? Transparantie hierover onderscheidt je onmiddellijk.',
      },
      {
        titel: 'Kennisartikelen',
        tekst:
          'Een artikel dat een concrete vraag beantwoordt bouwt autoriteit op en wordt gevonden door mensen die nog niet klaar zijn om te bellen.',
      },
    ],
    vindbaarheid:
      'Op brede termen als “financieel adviseur” concurreer je met vergelijkingssites en grote ketens. Op specifieke vragen binnen jouw specialisme is er nauwelijks concurrentie, en dat zijn precies de mensen die klaar zijn voor een gesprek.',
    caseSlug: 'mijn-bedrijfs-waarderingen',
    caseNaam: 'Mijn Bedrijfs Waarderingen',
    caseTekst:
      'Een nieuw label dat bedrijfswaardering als eigen propositie in de markt zet, met een samenwerkingstool voor adviseurs.',
    faq: [
      {
        vraag: 'Mag ik klantnamen noemen?',
        antwoord:
          'Alleen met toestemming, en in deze branche geven klanten die niet snel. Werk daarom met geanonimiseerde casusbeschrijvingen, die geven hetzelfde vertrouwen.',
      },
      {
        vraag: 'Moet ik tarieven vermelden?',
        antwoord:
          'Een indicatie of het uurtarief helpt. Wie geen enkel idee heeft van de kosten neemt vaak geen contact op, uit angst voor een verrassing.',
      },
      {
        vraag: 'Hoeveel artikelen moet ik schrijven?',
        antwoord:
          'Twee tot vier per maand is realistisch naast je praktijk. Consistentie weegt zwaarder dan aantal, en je kunt de inhoud aanleveren terwijl wij het schrijfwerk doen.',
      },
    ],
  },
  {
    slug: 'mediator',
    sector: 'Mediation',
    h1: 'Website laten maken voor een mediator',
    metaTitle: 'Website laten maken voor een mediator | Noord Digital',
    metaDescription:
      'Een mediationsite die rust uitstraalt en toch aanvragen oplevert. Met een pagina per type conflict en een lage drempel om contact op te nemen.',
    intro:
      'Wie een mediator zoekt, zit zelden lekker in zijn vel. Een scheiding, een arbeidsconflict, een ruzie binnen de familie. Je site moet in de eerste seconden rust geven, en tegelijk duidelijk maken dat je weet waar je het over hebt.',
    herkenbaar: [
      'Je aanvragen komen vooral via advocaten en werkgevers, niet rechtstreeks.',
      'Je site is zakelijk maar voelt kil voor mensen in een lastige situatie.',
      'Alle vormen van mediation staan op één pagina bij elkaar.',
      'Mensen weten niet wat mediation kost en vragen er daarom niet naar.',
    ],
    onderdelen: [
      {
        titel: 'Een pagina per type conflict',
        tekst:
          'Echtscheiding, arbeidsconflict en familiemediation kennen andere vragen en andere emoties. Ze horen niet op dezelfde pagina.',
      },
      {
        titel: 'Uitleg wat mediation wél en niet is',
        tekst:
          'Veel mensen verwarren het met een advocaat of een rechtszaak. Dat verschil helder uitleggen is de belangrijkste tekst op je site.',
      },
      {
        titel: 'Registratie zichtbaar',
        tekst:
          'MfN-registratie is een vertrouwenssignaal waar mensen actief op zoeken in een markt met veel onerkende aanbieders.',
      },
      {
        titel: 'Kosten en duur',
        tekst:
          'Hoeveel gesprekken zijn er gemiddeld nodig en wat kost een traject? Dit is de vraag die mensen niet durven stellen.',
      },
      {
        titel: 'Rustig contactmoment',
        tekst:
          'Een korte, vriendelijke eerste stap. Geen groot formulier met tien verplichte velden.',
      },
    ],
    vindbaarheid:
      'Er wordt gezocht in halve zinnen en op situaties: “mediator of advocaat”, “hoe lang duurt mediation”, “scheidingsmediator Alkmaar”. Content die die vragen letterlijk beantwoordt wordt gevonden door mensen die nog aan het oriënteren zijn.',
    caseSlug: 'flow-frl',
    caseNaam: 'FLOW Mediation',
    caseTekst:
      'Een kalme, professionele site die vertrouwen uitstraalt en zorgt voor een constante stroom aan aanvragen.',
    faq: [
      {
        vraag: 'Mag ik zaken beschrijven op mijn site?',
        antwoord:
          'Alleen volledig geanonimiseerd en zonder herleidbare details. Beschrijf liever het type situatie en hoe een traject verloopt dan een concrete zaak.',
      },
      {
        vraag: 'Moet ik tarieven noemen?',
        antwoord:
          'Wij adviseren van wel, minstens een uurtarief of een indicatie per traject. Mensen in een conflict hebben al genoeg onzekerheid.',
      },
      {
        vraag: 'Hoe voorkom ik dat de site te koud aanvoelt?',
        antwoord:
          'Met rustige kleuren, veel witruimte en tekst die de situatie erkent voordat hij over de dienst begint. Toon en tempo doen hier meer dan vormgeving.',
      },
    ],
  },
]

export const getSectorPageBySlug = (slug: string): SectorPage | undefined =>
  sectorPages.find((p) => p.slug === slug)
